/**
 * VideoCallContext — estado global da chamada de vídeo do atendente (VCALL-33, 37).
 *
 * Fica no shell (acima do router, como o `ActiveCallContext`): trocar de rota não desmonta o
 * provider, então a janela da chamada continua flutuando sem reconectar a mídia.
 *
 * - `call`: a chamada atual (resposta do `POST /video-calls` + dados do contato) e o estado vindo
 *   dos sockets `video-call:*`; `connection`: credenciais LiveKit (`token`, `wsUrl`, `participantId`).
 * - `resumableCall`: chamada não terminal encontrada ao abrir a plataforma — a UI oferece voltar.
 * - `setup`: pedido de abertura do modal de configuração (botão do chat ou reabertura após
 *   `APP_NO_ANSWER`, sem a opção app e mantendo o modo de entrada — VCALL-59, 72).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useSocket } from '@/contexts/SocketContext';
import { useAuth } from '@/contexts/AuthContext';
import { getActiveVideoCall, rejoinVideoCall } from '@/services/videoCallApi';
import { isMissingOttoLicense } from '@/lib/ottoLicense';
import { declinedToast } from '@/components/videoCall/recording/recordingCopy';

const VideoCallContext = createContext(null);

export const VIDEO_CALL_FEATURE = 'calling.video';

/** Aviso PT-BR quando a tentativa pelo app termina sem o contato entrar (VCALL-59, 72; AC-4.10a). */
export function appNoAnswerNotice(endReason) {
    return endReason === 'APP_UNAVAILABLE' || endReason === 'APP_DELIVERY_FAILED'
        ? 'O app do contato está indisponível'
        : 'O contato não respondeu no app';
}

/**
 * Acesso do usuário à chamada de vídeo: quem tem o Otto usa, e quem não tem não usa. Não há permissão de perfil à parte.
 * Ter o Otto é o plano Otto da pessoa (`viaPlan`, dado na tela de Usuários), a licença de uma conta de plano por licença, ou o
 * vídeo ligado no plano ou no addon da conta (`calling.video`).
 * @returns {{ hasAddon: boolean, licensed: boolean }} `hasAddon` quer dizer "o vídeo está liberado para esta pessoa": pelo plano
 *   Otto dela, pela licença OU pela conta (o nome ficou do tempo em que só havia o addon). `licensed` é falso só quando a
 *   conta exige licença e esta pessoa ainda não tem (a API recusa com 403 `OTTO_LICENSE_REQUIRED`; a tela nem pergunta)
 */
export function useVideoCallAccess() {
    // `hasPlanFeature` e não `hasFeature`: o trial libera todas as chaves, e o vídeo é do Otto (quem testa usa o Otto Gratuito)
    const { hasPlanFeature, ottoLicense } = useAuth();
    const viaOttoPlan = ottoLicense?.viaPlan === true;
    return {
        hasAddon: Boolean(hasPlanFeature?.(VIDEO_CALL_FEATURE)) || viaOttoPlan,
        licensed: !isMissingOttoLicense(ottoLicense),
    };
}

export function VideoCallProvider({ children }) {
    const { socket } = useSocket();
    const { user } = useAuth();
    const access = useVideoCallAccess();
    const canUseVideo = Boolean(user) && access.hasAddon && access.licensed;

    const [call, setCall] = useState(null);
    const [connection, setConnection] = useState(null);
    const [waitingGuests, setWaitingGuests] = useState([]);
    const [minutesWarningSeconds, setMinutesWarningSeconds] = useState(null);
    const [resumableCall, setResumableCall] = useState(null);
    const [setup, setSetup] = useState(null);

    const callRef = useRef(call);
    callRef.current = call;

    // Retomada (VCALL-37): uma vez por sessão do usuário.
    const checkedUserRef = useRef(null);
    useEffect(() => {
        if (!canUseVideo || checkedUserRef.current === user.id) return;
        checkedUserRef.current = user.id;
        let cancelled = false;
        getActiveVideoCall()
            .then((active) => {
                if (!cancelled && active && !callRef.current) setResumableCall(active);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [canUseVideo, user?.id]);

    const openSetup = useCallback((params) => setSetup({ excludeApp: false, entryMode: null, notice: null, ...params }), []);
    const closeSetup = useCallback(() => setSetup(null), []);

    /**
     * Chamada criada pelo modal de configuração.
     * @param {object} created - resposta do `POST /video-calls`
     * @param {{ contactId: string, contactName?: string, ticketId?: string|null, channelName?: string|null, channelType?: string|null }} info
     */
    const beginCall = useCallback((created, info = {}) => {
        const { token, wsUrl, participantId, ...rest } = created;
        setCall({ ...rest, ...info, createdAt: rest.createdAt ?? new Date().toISOString(), endReason: null, appStatus: null });
        setConnection({ token, wsUrl, participantId });
        setWaitingGuests([]);
        setMinutesWarningSeconds(null);
        setResumableCall(null);
        setSetup(null);
    }, []);

    /** Volta à chamada encontrada ao abrir a plataforma (novo token). */
    const resumeCall = useCallback(async () => {
        const target = resumableCall;
        if (!target) return;
        try {
            const { joinUrl, ...credentials } = await rejoinVideoCall(target.id);
            setCall({ ...target, joinUrl: joinUrl ?? target.joinUrl ?? null, appStatus: null });
            setConnection(credentials);
            setWaitingGuests([]);
            setResumableCall(null);
        } catch (err) {
            setResumableCall(null);
            toast.error(err?.code === 'VIDEO_CALL_ENDED' ? 'A chamada já foi encerrada' : 'Não foi possível voltar à chamada');
        }
    }, [resumableCall]);

    /** Entra de novo numa sala `LINK` ainda válida (bolha do chat, histórico — VCALL-17). */
    const rejoinCall = useCallback(async (summary) => {
        // Numa sala por link o servidor devolve o `joinUrl`: o atendente que volta ainda copia e divide o link.
        const { joinUrl, ...credentials } = await rejoinVideoCall(summary.id);
        setCall({ ...summary, joinUrl: joinUrl ?? summary.joinUrl ?? null, appStatus: null });
        setConnection(credentials);
        setWaitingGuests([]);
        setMinutesWarningSeconds(null);
        setResumableCall(null);
    }, []);

    const dismissResumable = useCallback(() => setResumableCall(null), []);

    /** Fecha a janela: chamada terminal, ou o atendente saiu de uma sala `LINK` que segue aberta. */
    const clearCall = useCallback(() => {
        setCall(null);
        setConnection(null);
        setWaitingGuests([]);
        setMinutesWarningSeconds(null);
    }, []);

    /**
     * O atendente saiu de uma sala `LINK` que segue aberta depois de a chamada ter acontecido: a
     * janela fica para a pesquisa de qualidade (`leftRoom`) em vez de sumir.
     */
    const leaveRoom = useCallback(() => {
        setCall((prev) => (prev ? { ...prev, leftRoom: true } : prev));
        setConnection(null);
        setWaitingGuests([]);
        setMinutesWarningSeconds(null);
    }, []);

    /** Estado final devolvido pela API (encerrar, "sair" após 10 min), sem esperar o socket. */
    const finishCall = useCallback(({ status, endReason = null }) => {
        setCall((prev) => (prev ? { ...prev, status, endReason } : prev));
        setConnection(null);
        setWaitingGuests([]);
    }, []);

    const removeWaitingGuest = useCallback((participantId) => {
        setWaitingGuests((prev) => prev.filter((g) => g.participantId !== participantId));
    }, []);

    /** Tentativa pelo app sem o contato: encerra e reabre o modal sem o app (VCALL-59, 72). */
    const handleAppNoAnswer = useCallback((current, endReason) => {
        setCall({ ...current, status: 'APP_NO_ANSWER', endReason: endReason ?? null });
        setConnection(null);
        setSetup({
            contactId: current.contactId,
            contactName: current.contactName,
            ticketId: current.ticketId ?? null,
            entryMode: current.entryMode,
            excludeApp: true,
            notice: appNoAnswerNotice(endReason),
        });
    }, []);

    useEffect(() => {
        if (!socket) return undefined;
        const isCurrent = (data) => {
            const current = callRef.current;
            return current && data?.videoCallId === current.id ? current : null;
        };

        const onUpdated = (data) => {
            const current = isCurrent(data);
            if (!current) return;
            if (data.participantId && data.admission) {
                setWaitingGuests((prev) => prev.filter((g) => g.participantId !== data.participantId));
                return;
            }
            if (data.status === 'APP_NO_ANSWER') {
                handleAppNoAnswer(current, data.endReason);
                return;
            }
            if (data.status) {
                setCall((prev) => (prev && prev.id === current.id
                    ? {
                        ...prev,
                        status: data.status,
                        appStatus: data.appStatus ?? prev.appStatus ?? null,
                        // A chamada aconteceu (atendente e contato na sala): decide a pesquisa de qualidade no fim.
                        wasActive: Boolean(prev.wasActive) || data.status === 'ACTIVE',
                    }
                    : prev));
            }
        };

        const onEnded = (data) => {
            const current = isCurrent(data);
            if (!current) return;
            if (data.status === 'APP_NO_ANSWER') {
                handleAppNoAnswer(current, data.endReason);
                return;
            }
            setCall((prev) => (prev && prev.id === current.id
                ? { ...prev, status: data.status, endReason: data.endReason ?? null }
                : prev));
            setConnection(null);
            setWaitingGuests([]);
        };

        const onWaiting = (data) => {
            if (!isCurrent(data)) return;
            setWaitingGuests((prev) => (prev.some((g) => g.participantId === data.participantId)
                ? prev
                : [...prev, { participantId: data.participantId, displayName: data.displayName }]));
        };

        const onGuestArrived = (data) => {
            toast.info(`${data?.displayName || 'Convidado'} entrou na sala e está aguardando você.`);
        };

        const onMinutesWarning = (data) => {
            if (!isCurrent(data)) return;
            setMinutesWarningSeconds(data.remainingSeconds);
        };

        // A reunião está perto da duração máxima do plano (otto.meeting_max_minutes): avisa para o atendente fechar o assunto
        const onMeetingWarning = (data) => {
            if (!isCurrent(data)) return;
            const minutes = Math.max(1, Math.ceil((data?.remainingSeconds ?? 0) / 60));
            toast.warning(`A reunião termina em ${minutes} min: é a duração máxima do plano.`);
        };

        socket.on('video-call:updated', onUpdated);
        socket.on('video-call:ended', onEnded);
        socket.on('video-call:participant_waiting', onWaiting);
        socket.on('video-call:guest_arrived', onGuestArrived);
        socket.on('video-call:minutes_warning', onMinutesWarning);
        // Um convidado não aceitou ser gravado: a sala só mostra que o pedido foi cancelado, quem recusou chega por aqui
        const onRecordingDeclined = (data) => {
            if (!isCurrent(data)) return;
            toast.warning(declinedToast(data?.displayName));
        };

        socket.on('video-call:meeting_warning', onMeetingWarning);
        socket.on('video-call:recording_declined', onRecordingDeclined);
        return () => {
            socket.off('video-call:updated', onUpdated);
            socket.off('video-call:ended', onEnded);
            socket.off('video-call:participant_waiting', onWaiting);
            socket.off('video-call:guest_arrived', onGuestArrived);
            socket.off('video-call:minutes_warning', onMinutesWarning);
            socket.off('video-call:meeting_warning', onMeetingWarning);
            socket.off('video-call:recording_declined', onRecordingDeclined);
        };
    }, [socket, handleAppNoAnswer]);

    const value = useMemo(() => ({
        call,
        connection,
        waitingGuests,
        minutesWarningSeconds,
        resumableCall,
        setup,
        hasVideoCall: Boolean(call),
        openSetup,
        closeSetup,
        beginCall,
        resumeCall,
        rejoinCall,
        dismissResumable,
        clearCall,
        leaveRoom,
        finishCall,
        removeWaitingGuest,
    }), [call, connection, waitingGuests, minutesWarningSeconds, resumableCall, setup,
        openSetup, closeSetup, beginCall, resumeCall, rejoinCall, dismissResumable, clearCall, leaveRoom, finishCall, removeWaitingGuest]);

    return <VideoCallContext.Provider value={value}>{children}</VideoCallContext.Provider>;
}

/** Fora do provider (HMR, telas isoladas): sem chamada e ações no-op, como o `useActiveCall`. */
const SAFE_DEFAULTS = Object.freeze({
    call: null,
    connection: null,
    waitingGuests: [],
    minutesWarningSeconds: null,
    resumableCall: null,
    setup: null,
    hasVideoCall: false,
    openSetup: () => {},
    closeSetup: () => {},
    beginCall: () => {},
    resumeCall: async () => {},
    rejoinCall: async () => {},
    dismissResumable: () => {},
    clearCall: () => {},
    leaveRoom: () => {},
    finishCall: () => {},
    removeWaitingGuest: () => {},
});

export function useVideoCall() {
    return useContext(VideoCallContext) ?? SAFE_DEFAULTS;
}

export default VideoCallContext;
